import { useEffect, useState } from 'react';
import { UserModel, UserRoleEnum, UserStatusEnum } from '../../../interfaces/user.interface';
import { TeacherOnboardingModel } from '../../../interfaces/teacher.interface';
import { BookModel } from '../../../interfaces/book.interface';
import { OrderDetailsModel } from '../../../interfaces/order.interface';
import { getTeacherClassroomsByAdmin, updateStartDateProjectByAdmin } from '../../../store/thunks/user-teacher.thunk';
import { AppDispatch, RootState } from '../../../store/store';
import { useDispatch, useSelector } from 'react-redux';
import { convertTimestampToDateTime, convertTimestampToDateTimeString, mapDateToLocalString } from '../../../helper/helper';
import NewDatePickerPopup, { DateSelectorMode } from '../../Teacher/components/NewDatePickerPopup';
import { SelectedDateTime } from '../../Teacher/components/PublishingTimelineForm';
import { setSelectedProjectInClassrooms } from '../../../store/userSlice';
import Loader from '../../../components/Common/Loader';
import { callAPI } from '../../../services/api.service';
import { getAdminUserBooks, getAdminUserByEmail } from '../../../services/AdminUser.service';
import { fetchBookPurchaseCounts, fetchUserOrders, shipSelectedOrder } from '../PhysicalBookOrder/services/PhysicalBookOrder.service';
import { resetFlyerGenerationService } from './services/AdminForms.service';
import {
  AdminBooksTab,
  AdminCharactersTab,
  AdminDetailsHeader,
  AdminSummaryCard,
  AdminTabs,
  AdminTabDef,
  SummaryField,
} from '../../../components/Admin';
import OrderDetailsTable from '../PhysicalBookOrder/components/OrderDetailsTable';
import { calculateOrderTotal, getSyncState, isPhysicalOrder, orderRouteLabel, orderTypeLabel, syncStateLabel, syncStateColor } from '../PhysicalBookOrder/helpers/orderDisplay';
import FulfillmentStatusBadges from '../PhysicalBookOrder/components/FulfillmentStatusBadges';
import PaginationButtons from '../../../components/Common/Pagination';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBox, faEye } from '@fortawesome/free-solid-svg-icons';
import { AdminNavHandler } from '../adminNav';

type TabType = 'classrooms' | 'books' | 'characters' | 'orders' | 'surveys' | 'lead';

const STUCK_THRESHOLD_MS = 10 * 60 * 1000;

interface FlyerStateShape {
  bookFlyersGenerationProgress: number | null | undefined;
  bookFlyersPdfPath: string | null | undefined;
  bookFlyersGenerationAt: any;
  preSessionFlyersGenerationProgress: number | null | undefined;
  preSessionFlyersPdfPath: string | null | undefined;
  preSessionFlyersGenerationAt: any;
}

const toMillis = (value: any): number | null => {
  if (!value) return null;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  if (value?.seconds) return value.seconds * 1000;
  if (value?._seconds) return value._seconds * 1000;
  if (value instanceof Date) return value.getTime();
  return null;
};

interface FlyerSideRowProps {
  label: string;
  progress: number | null | undefined;
  pdfPath: string | null | undefined;
  generatedAt: any;
  resetting: boolean;
  disabled: boolean;
  onReset: () => void;
}

const FlyerSideRow = ({ label, progress, pdfPath, generatedAt, resetting, disabled, onReset }: FlyerSideRowProps) => {
  const inProgress = typeof progress === 'number' && progress >= 0 && progress < 100;
  const generatedAtMs = toMillis(generatedAt);
  const ageMs = generatedAtMs ? Date.now() - generatedAtMs : null;
  const isStuck = inProgress && ageMs !== null && ageMs >= STUCK_THRESHOLD_MS;

  return (
    <div className="flex items-center justify-between gap-4 py-2 border-b border-gray-100 last:border-0">
      <div className="flex flex-col text-sm">
        <span className="font-medium text-gray-800">{label}</span>
        <span className="text-xs text-gray-600">
          {inProgress ? (
            <>
              In progress: <span className={isStuck ? 'text-red-600 font-semibold' : 'text-gray-800'}>{progress}%</span>
              {generatedAtMs && (
                <> · started {new Date(generatedAtMs).toLocaleString()}</>
              )}
              {isStuck && <span className="ml-2 text-red-600 font-semibold">(likely stuck)</span>}
            </>
          ) : pdfPath ? (
            <>
              Last generated{generatedAtMs ? `: ${new Date(generatedAtMs).toLocaleString()}` : ''} ·{' '}
              <a href={pdfPath} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                Open PDF
              </a>
            </>
          ) : (
            <span className="text-gray-500">No flyer generated yet</span>
          )}
        </span>
      </div>
      <button
        type="button"
        disabled={disabled}
        onClick={onReset}
        className="text-xs px-3 py-1.5 rounded border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {resetting ? 'Resetting…' : 'Reset'}
      </button>
    </div>
  );
};

interface FlyerGenerationPanelProps {
  flyerState: FlyerStateShape;
  resetting: 'pre' | 'post' | 'all' | null;
  refreshing: boolean;
  onReset: (type: 'pre' | 'post' | 'all') => void;
}

const FlyerGenerationPanel = ({ flyerState, resetting, refreshing, onReset }: FlyerGenerationPanelProps) => {
  const anyResetting = resetting !== null;
  return (
    <div className="my-4 p-4 bg-white border border-gray-200 rounded-md">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-bold text-storyblue">
          Flyer Generation
          {refreshing && <span className="ml-2 text-xs font-normal text-gray-500">refreshing…</span>}
        </h3>
        <button
          type="button"
          disabled={anyResetting}
          onClick={() => onReset('all')}
          className="text-xs px-3 py-1.5 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {resetting === 'all' ? 'Resetting…' : 'Reset all flyers'}
        </button>
      </div>
      <FlyerSideRow
        label="Pre-session flyer"
        progress={flyerState.preSessionFlyersGenerationProgress}
        pdfPath={flyerState.preSessionFlyersPdfPath}
        generatedAt={flyerState.preSessionFlyersGenerationAt}
        resetting={resetting === 'pre'}
        disabled={anyResetting}
        onReset={() => onReset('pre')}
      />
      <FlyerSideRow
        label="Post-session flyer"
        progress={flyerState.bookFlyersGenerationProgress}
        pdfPath={flyerState.bookFlyersPdfPath}
        generatedAt={flyerState.bookFlyersGenerationAt}
        resetting={resetting === 'post'}
        disabled={anyResetting}
        onReset={() => onReset('post')}
      />
    </div>
  );
};

interface CustomerDetailsViewProps {
  user: UserModel;
  isOpen: boolean;
  onClose: () => void;
  onOptionClick?: AdminNavHandler;
}

interface SurveyData extends TeacherOnboardingModel {
  id?: string;
  userEmail?: string;
  createdAt?: any;
  updatedAt?: any;
}

const CustomerDetailsView = ({ user, isOpen, onClose, onOptionClick }: CustomerDetailsViewProps) => {
  const dispatch: AppDispatch = useDispatch();
  const classrooms = useSelector((state: RootState) => state.user.classrooms);
  const isTeacherUser = user?.role === UserRoleEnum.teacher || !!user?.settings?.USER_SETTING_TEACHER_OR_GROUP;

  const [activeTab, setActiveTab] = useState<TabType>(isTeacherUser ? 'classrooms' : 'books');
  const [classroomsLoader, setClassroomsLoader] = useState(false);
  const [surveysLoader, setSurveysLoader] = useState(false);
  const [surveys, setSurveys] = useState<SurveyData[]>([]);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedClassroomId, setSelectedClassroomId] = useState<string | null>(null);
  const [charactersRefreshKey, setCharactersRefreshKey] = useState(0);

  // Books state
  const [books, setBooks] = useState<BookModel[]>([]);
  const [booksLoader, setBooksLoader] = useState(false);
  const [booksPageNumber, setBooksPageNumber] = useState(1);
  const [purchaseCounts, setPurchaseCounts] = useState<Record<string, number>>({});
  const [totalBooksPages, setTotalBooksPages] = useState<number>(0);

  // Flyer generation state — mirrored from user so resets reflect immediately
  const [flyerState, setFlyerState] = useState({
    bookFlyersGenerationProgress: user?.bookFlyersGenerationProgress ?? null,
    bookFlyersPdfPath: user?.bookFlyersPdfPath ?? null,
    bookFlyersGenerationAt: user?.bookFlyersGenerationAt ?? null,
    preSessionFlyersGenerationProgress: user?.preSessionFlyersGenerationProgress ?? null,
    preSessionFlyersPdfPath: user?.preSessionFlyersPdfPath ?? null,
    preSessionFlyersGenerationAt: user?.preSessionFlyersGenerationAt ?? null,
  });
  const [flyerResetting, setFlyerResetting] = useState<'pre' | 'post' | 'all' | null>(null);
  const [flyerRefreshing, setFlyerRefreshing] = useState(false);

  // Orders state
  const [orders, setOrders] = useState<OrderDetailsModel[]>([]);
  const [ordersLoader, setOrdersLoader] = useState(false);
  const [ordersPageNumber, setOrdersPageNumber] = useState(1);
  const [totalOrdersPages, setTotalOrdersPages] = useState<number>(0);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState<OrderDetailsModel | null>(null);

  useEffect(() => {
    if (user && user.email && isTeacherUser) {
      setClassroomsLoader(true);
      dispatch(getTeacherClassroomsByAdmin({ teacherEmail: user.email })).finally(() => setClassroomsLoader(false));
    }
    setFlyerState({
      bookFlyersGenerationProgress: user?.bookFlyersGenerationProgress ?? null,
      bookFlyersPdfPath: user?.bookFlyersPdfPath ?? null,
      bookFlyersGenerationAt: user?.bookFlyersGenerationAt ?? null,
      preSessionFlyersGenerationProgress: user?.preSessionFlyersGenerationProgress ?? null,
      preSessionFlyersPdfPath: user?.preSessionFlyersPdfPath ?? null,
      preSessionFlyersGenerationAt: user?.preSessionFlyersGenerationAt ?? null,
    });
  }, [user]);

  const handleResetFlyer = async (type: 'pre' | 'post' | 'all') => {
    if (!user?.email) return;
    const label = type === 'all' ? 'all flyers' : type === 'pre' ? 'pre-session flyers' : 'post-session flyers';
    if (!window.confirm(`Reset ${label} for ${user.email}?\n\nThis clears the in-progress and last-generated state so the teacher can retry.`)) return;
    setFlyerResetting(type);
    const ok = await resetFlyerGenerationService(user.email, type);
    if (ok) {
      setFlyerState((prev) => ({
        ...prev,
        ...(type === 'post' || type === 'all'
          ? { bookFlyersGenerationProgress: null, bookFlyersPdfPath: null, bookFlyersGenerationAt: null }
          : {}),
        ...(type === 'pre' || type === 'all'
          ? { preSessionFlyersGenerationProgress: null, preSessionFlyersPdfPath: null, preSessionFlyersGenerationAt: null }
          : {}),
      }));
    }
    setFlyerResetting(null);
  };

  useEffect(() => {
    if (activeTab === 'surveys' && user?.email && surveys.length === 0) {
      fetchSurveys();
    }
    if (activeTab === 'books' && user?.email && books.length === 0) {
      fetchBooks();
    }
    if (activeTab === 'orders' && user?.email && orders.length === 0) {
      fetchOrders();
    }
  }, [activeTab]);

  const refreshFlyerFromServer = async () => {
    if (!user?.email || !isTeacherUser) return;
    setFlyerRefreshing(true);
    try {
      const fresh = await getAdminUserByEmail(user.email);
      if (fresh) {
        setFlyerState({
          bookFlyersGenerationProgress: fresh.bookFlyersGenerationProgress ?? null,
          bookFlyersPdfPath: fresh.bookFlyersPdfPath ?? null,
          bookFlyersGenerationAt: fresh.bookFlyersGenerationAt ?? null,
          preSessionFlyersGenerationProgress: fresh.preSessionFlyersGenerationProgress ?? null,
          preSessionFlyersPdfPath: fresh.preSessionFlyersPdfPath ?? null,
          preSessionFlyersGenerationAt: fresh.preSessionFlyersGenerationAt ?? null,
        });
      }
    } finally {
      setFlyerRefreshing(false);
    }
  };

  // Admin plan assignment: same free-subscription path teacher signup uses (no Stripe billing
  // changes — paid subscriptions are managed in Stripe). Local override shows the new plan name
  // immediately; the row list refreshes via the cleared admin cache on next load.
  const [assignedPlanName, setAssignedPlanName] = useState<string | null>(null);
  const [planPickerOpen, setPlanPickerOpen] = useState(false);
  const [planOptions, setPlanOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [assigningPlan, setAssigningPlan] = useState(false);

  const openPlanPicker = async () => {
    setPlanPickerOpen(true);
    if (!planOptions.length) {
      const response = await callAPI('plans/getPlans', null, 'GET');
      const plans = (response?.plans || []).map((p: any) => ({ id: p.id, name: p.name || p.title || p.id }));
      setPlanOptions(plans);
    }
  };

  const handleAssignPlan = async () => {
    if (!user?.email || !selectedPlanId) return;
    const planName = planOptions.find((p) => p.id === selectedPlanId)?.name || selectedPlanId;
    if (!window.confirm(`Assign plan "${planName}" to ${user.email}? This applies the plan's credits/limits but does NOT change any Stripe billing.`)) return;
    setAssigningPlan(true);
    try {
      const response = await callAPI('admin/user/assign-plan', { email: user.email, planId: selectedPlanId });
      if (response?.status === 200) {
        setAssignedPlanName(response.planName || planName);
        setPlanPickerOpen(false);
      } else {
        window.alert(response?.error || 'Plan assignment failed');
      }
    } finally {
      setAssigningPlan(false);
    }
  };

  const handleRefresh = () => {
    if (activeTab === 'books') { setBooks([]); fetchBooks(); }
    else if (activeTab === 'orders') { setOrders([]); fetchOrders(); }
    else if (activeTab === 'surveys') { setSurveys([]); fetchSurveys(); }
    else if (activeTab === 'characters') setCharactersRefreshKey((k) => k + 1);
    else if (activeTab === 'classrooms' && user?.email && isTeacherUser) {
      setClassroomsLoader(true);
      dispatch(getTeacherClassroomsByAdmin({ teacherEmail: user.email })).finally(() => setClassroomsLoader(false));
    }
    refreshFlyerFromServer();
  };

  const isRefreshing = booksLoader || ordersLoader || surveysLoader || classroomsLoader || flyerRefreshing;

  const fetchSurveys = async () => {
    if (!user?.email) return;
    try {
      setSurveysLoader(true);
      const result = await callAPI<null>(
        `admin/user/surveys?email=${encodeURIComponent(user.email)}`,
        null,
        'GET',
      );
      if (result?.data) {
        setSurveys(result.data);
      }
    } catch (error) {
      console.error('Failed to fetch surveys:', error);
    } finally {
      setSurveysLoader(false);
    }
  };

  const fetchBooks = async () => {
    if (!user?.email) return;
    try {
      setBooksLoader(true);
      const [{ books: fetchedBooks, totalBooksPages: pages }, counts] = await Promise.all([
        getAdminUserBooks(user.email, booksPageNumber, true),
        fetchBookPurchaseCounts(user.email),
      ]);
      setBooks(fetchedBooks);
      setTotalBooksPages(pages);
      setPurchaseCounts(counts);
    } catch (error) {
      console.error('Failed to fetch books:', error);
    } finally {
      setBooksLoader(false);
    }
  };

  const handleBooksPageChange = (page: number | string) => {
    const p = +page;
    if (p > 0 && p <= totalBooksPages) {
      setBooksPageNumber(p);
    }
  };

  useEffect(() => {
    if (activeTab === 'books' && user?.email) {
      fetchBooks();
    }
  }, [booksPageNumber]);

  useEffect(() => {
    if (activeTab === 'orders' && user?.email) {
      fetchOrders();
    }
  }, [ordersPageNumber]);

  const fetchOrders = async () => {
    if (!user?.email) return;
    try {
      setOrdersLoader(true);
      const { orders: fetchedOrders, totalPages } = await fetchUserOrders(user.email, ordersPageNumber);
      setOrders(fetchedOrders);
      setTotalOrdersPages(totalPages);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setOrdersLoader(false);
    }
  };

  const handleOrdersPageChange = (page: number | string) => {
    const p = +page;
    if (p > 0 && p <= totalOrdersPages) {
      setOrdersPageNumber(p);
    }
  };

  const calculateBookPurchaseTotalAmount = (order: OrderDetailsModel) => calculateOrderTotal(order);

  const handleShipOrder = async (selectedOrder: OrderDetailsModel) => {
    if (!selectedOrder?.orderNumber) return;
    await shipSelectedOrder(selectedOrder.orderNumber, user?.email, (loader) =>
      setOrders((prevState) =>
        prevState.map((item) =>
          item.orderNumber === selectedOrder.orderNumber ? { ...item, shipLoading: loader } : item
        )
      )
    );
  };

  const handleStartDateClick = (classroomId: string) => {
    setSelectedClassroomId(classroomId);
    setShowDatePicker(true);
  };

  const handleProjectChange = async (classroomId: string, projectId: string) => {
    dispatch(setSelectedProjectInClassrooms({ classroomId, projectId }));
  };

  const getSelectedClassroom = () => {
    return classrooms?.find((classroom) => classroom.id === selectedClassroomId);
  };

  const handleDateSelected = async (date: SelectedDateTime, mode: DateSelectorMode) => {
    setShowDatePicker(false);
    setSelectedClassroomId(null);
    const projectId = getSelectedClassroom()?.activeProject?.id;
    if (!projectId) return;

    await dispatch(updateStartDateProjectByAdmin({ projectId, startDateData: date }));

    setClassroomsLoader(true);
    user?.email &&
      dispatch(getTeacherClassroomsByAdmin({ teacherEmail: user.email })).finally(() => setClassroomsLoader(false));
  };

  const renderSurveyField = (label: string, value: any) => {
    if (value === undefined || value === null || value === '') return null;

    let displayValue = value;
    if (typeof value === 'boolean') {
      displayValue = value ? 'Yes' : 'No';
    } else if (Array.isArray(value)) {
      displayValue = value.join(', ');
    } else if (typeof value === 'object') {
      displayValue = JSON.stringify(value, null, 2);
    }

    return (
      <div className="flex justify-between py-1.5 border-b border-gray-100 last:border-0" key={label}>
        <span className="text-sm text-gray-600 capitalize">{label.replace(/([A-Z])/g, ' $1').trim()}:</span>
        <span className="text-sm font-medium text-gray-800 text-right max-w-[60%]">
          {typeof displayValue === 'string' && displayValue.length > 100 ? (
            <pre className="text-xs whitespace-pre-wrap text-left">{displayValue}</pre>
          ) : (
            String(displayValue)
          )}
        </span>
      </div>
    );
  };

  if (!isOpen) return null;

  const bookBalance = +(user?.booksCreateLimit || 0) - +(user?.booksCreatedCounter || 0);

  const tabDef: AdminTabDef<TabType>[] = [
    { key: 'classrooms', label: 'Classrooms', show: isTeacherUser },
    { key: 'books', label: 'Books' },
    { key: 'characters', label: 'Characters' },
    { key: 'orders', label: 'Orders' },
    { key: 'lead', label: 'Leads', show: !!user.lead },
    { key: 'surveys', label: 'Surveys', show: isTeacherUser },
  ];

  const statusColor = user.status === UserStatusEnum.ACTIVE ? 'text-green-600' : 'text-red-500';

  return (
    <div className="max-w-5xl mx-auto min-h-screen">
      <AdminDetailsHeader
        title={`${isTeacherUser ? 'Teacher' : 'Customer'} Details — ${user.firstName || ''} ${user.lastName || ''}`.trim()}
        onClose={onClose}
        onRefresh={handleRefresh}
        refreshing={isRefreshing}
      />

      <AdminSummaryCard>
          <SummaryField label="Email" value={user.email} />
          <SummaryField label="Name" value={`${user.firstName || ''} ${user.lastName || ''}`.trim()} />
          <SummaryField label="Role" value={<span className="capitalize">{user.role?.replace('-', ' ')}</span>} />
          <SummaryField label="Status" value={<span className={`font-semibold capitalize ${statusColor}`}>{user.status || 'unknown'}</span>} />
          <SummaryField
            label="Plan"
            value={
              <span className="flex items-center gap-2">
                <span className="capitalize">{assignedPlanName || user.plan?.name || 'None'}</span>
                {!planPickerOpen && (
                  <button
                    type="button"
                    onClick={openPlanPicker}
                    className="text-xs text-storyblueButton underline"
                    title="Assign a plan (credits/limits only — Stripe billing is unaffected)"
                  >
                    change
                  </button>
                )}
                {planPickerOpen && (
                  <span className="flex items-center gap-1">
                    <select
                      value={selectedPlanId}
                      onChange={(e) => setSelectedPlanId(e.target.value)}
                      className="border border-gray-300 rounded px-1 py-0.5 text-xs max-w-[180px]"
                    >
                      <option value="">Pick a plan…</option>
                      {planOptions.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={handleAssignPlan}
                      disabled={!selectedPlanId || assigningPlan}
                      className="text-xs bg-storyblue text-white rounded px-2 py-0.5 disabled:opacity-50"
                    >
                      {assigningPlan ? '…' : 'Assign'}
                    </button>
                    <button type="button" onClick={() => setPlanPickerOpen(false)} className="text-xs text-gray-500">
                      cancel
                    </button>
                  </span>
                )}
              </span>
            }
          />
          <SummaryField label="Book Balance" value={bookBalance} />
          <SummaryField label="Signed Up" value={user.timestamp ? convertTimestampToDateTime(user.timestamp) : '—'} />
          <SummaryField label="Last Session" value={user.lastSessionAt ? convertTimestampToDateTime(user.lastSessionAt) : '—'} />
          {isTeacherUser ? (
            <>
              <SummaryField
                label="Onboarding"
                value={
                  <span className="capitalize">
                    {user.teacherOnboardingStatus
                      ? user.teacherOnboardingStatus.replace(/_/g, ' ')
                      : '—'}
                  </span>
                }
              />
              <SummaryField label="Lead ID" value={user.lead?.leadId} />
            </>
          ) : (
            <>
              <SummaryField label="Plan Expires" value={user.planExpireAt ? convertTimestampToDateTime(user.planExpireAt) : '—'} />
              {user.lead?.leadId && <SummaryField label="Lead ID" value={user.lead.leadId} />}
            </>
          )}
      </AdminSummaryCard>

      {isTeacherUser && (
        <FlyerGenerationPanel
          flyerState={flyerState}
          resetting={flyerResetting}
          refreshing={flyerRefreshing}
          onReset={handleResetFlyer}
        />
      )}

      <AdminTabs<TabType> tabs={tabDef} activeTab={activeTab} onChange={setActiveTab} />

      {/* Classrooms Tab */}
      {activeTab === 'classrooms' && (
        <div>
          {classroomsLoader && (
            <div className="flex justify-center py-8">
              <Loader loader={classroomsLoader} dimentions={'w-8 h-8'} />
            </div>
          )}
          {!classroomsLoader && classrooms && classrooms.length > 0 ? (
            <div className="space-y-3">
              {classrooms.map((classroom) => (
                <div
                  key={classroom.id}
                  className="w-full flex justify-between items-center p-3 bg-gray-50 rounded-md"
                >
                  <div className="flex flex-col">
                    {onOptionClick ? (
                      <button
                        type="button"
                        className="font-medium text-blue-600 hover:underline text-left"
                        title="Open classroom"
                        onClick={(e) => {
                          e.stopPropagation();
                          onClose();
                          onOptionClick('Classrooms', { classroomId: classroom.id });
                        }}
                      >
                        {classroom.name}
                      </button>
                    ) : (
                      <span className="font-medium text-gray-800">{classroom.name}</span>
                    )}
                    {classroom.groupCode && (
                      <span className="text-xs text-gray-500 font-mono">Code: {classroom.groupCode}</span>
                    )}
                    {classroom.description && <span className="text-sm text-gray-600">{classroom.description}</span>}
                    {onOptionClick && (
                      <button
                        type="button"
                        className="text-xs text-blue-600 hover:underline mt-1 self-start"
                        onClick={(e) => {
                          e.stopPropagation();
                          onClose();
                          onOptionClick('Students', { classroomId: classroom.id });
                        }}
                      >
                        View students
                      </button>
                    )}
                  </div>
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-800">Active Assignment:</span>
                      <select
                        value={classroom.activeProject?.id || ''}
                        onChange={(e) => handleProjectChange(classroom.id, e.target.value)}
                        className="bg-white border border-gray-300 rounded px-2 py-1 text-sm"
                      >
                        <option value="">Select Project</option>
                        {classroom.projects?.map((project) => (
                          <option
                            key={project.id}
                            value={project.id}
                            className={project.id === classroom.activeProject?.id ? 'bg-blue-100 font-semibold' : ''}
                          >
                            {project.id === classroom.activeProjectId ? `Active: ${project.name}` : project.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <span
                      className="text-sm text-gray-600 hover:underline hover:cursor-pointer"
                      onClick={() => handleStartDateClick(classroom.id)}
                    >
                      Start Date:{' '}
                      {classroom.activeProject?.startDate
                        ? mapDateToLocalString(classroom.activeProject?.startDate)
                        : 'Select Start Date'}
                    </span>
                  </div>
                  <div className="text-right">
                    {classroom.groupCode && (
                      <span className="text-sm text-gray-600">Group Code: {classroom.groupCode}</span>
                    )}
                    {classroom.createdAt && (
                      <div className="text-xs text-gray-500">
                        Created: {mapDateToLocalString(classroom.createdAt)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            !classroomsLoader && (
              <div className="text-center text-gray-500 py-8">No classrooms found.</div>
            )
          )}
        </div>
      )}

      {/* Books Tab */}
      {activeTab === 'books' && (
        <div>
          {booksLoader ? (
            <div className="flex justify-center py-8">
              <Loader loader={booksLoader} dimentions={'w-8 h-8'} />
            </div>
          ) : (
            <AdminBooksTab
              books={books}
              currentPage={booksPageNumber}
              totalBooksPages={totalBooksPages}
              handleBooksPageChange={handleBooksPageChange}
              purchaseCounts={purchaseCounts}
            />
          )}
        </div>
      )}

      {/* Characters Tab */}
      {activeTab === 'characters' && (
        <AdminCharactersTab email={user?.email} active={activeTab === 'characters'} refreshKey={charactersRefreshKey} />
      )}

      {/* Orders Tab */}
      {activeTab === 'orders' && (
        <div>
          {ordersLoader ? (
            <div className="flex justify-center py-8">
              <Loader loader={ordersLoader} dimentions={'w-8 h-8'} />
            </div>
          ) : selectedOrderDetails ? (
            <div>
              <button
                type="button"
                className="flex items-center gap-1 text-sm text-storyblue hover:underline mb-4"
                onClick={(e) => { e.stopPropagation(); setSelectedOrderDetails(null); }}
              >
                ← Back to all orders
              </button>
              <h3 className="text-sm font-bold text-storyblue mb-3">Order — Shipping Details</h3>
              <OrderDetailsTable selectedOrderDetails={selectedOrderDetails} email={user?.email} />
            </div>
          ) : orders.length > 0 ? (
            <div className="w-full overflow-auto">
              <table className="min-w-full border border-gray-300">
                <thead className="bg-gray-200 sticky top-0">
                  <tr>
                    {['Date', 'Description', 'Qty', 'Order Number', 'Total', 'Country', 'Route', 'Sync', 'Status', ''].map((h) => (
                      <th key={h} className="py-2 px-4 text-left text-sm font-semibold text-gray-700 border-b">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-gray-100' : ''}>
                      <td className="py-2 text-left px-4 border-b">{convertTimestampToDateTime(order?.timestamp)}</td>
                      <td className="py-2 text-left px-4 border-b">{orderTypeLabel(order?.orderType)}</td>
                      <td className="py-2 text-left px-4 border-b">{order?.quantity}</td>
                      <td className="py-2 text-left px-4 border-b">
                        {order?.orderNumber ? (
                          <span
                            className="cursor-pointer underline text-storygreen"
                            onClick={(e) => { e.stopPropagation(); setSelectedOrderDetails(order); }}
                          >
                            {order.orderNumber}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="py-2 text-left px-4 border-b">${calculateBookPurchaseTotalAmount(order)}</td>
                      <td className="py-2 text-left px-4 border-b">
                        {isPhysicalOrder(order?.orderType) ? order?.shippingCountry || '-' : '-'}
                      </td>
                      <td className="py-2 text-left px-4 border-b">
                        {!isPhysicalOrder(order?.orderType) ? (
                          '-'
                        ) : getSyncState(order) === 'pending' ? (
                          // Not sent yet → no route is decided. Show the pending state, not a guessed provider.
                          <span className="text-gray-400" title="Route is assigned when the order is sent to a provider">
                            Pending
                          </span>
                        ) : (
                          <span
                            className={`inline-block rounded px-2 py-0.5 text-xs font-semibold ${
                              orderRouteLabel(order) === 'ShipStation' ? 'bg-gray-200 text-gray-700' : 'bg-amber-100 text-amber-800'
                            }`}
                          >
                            {orderRouteLabel(order)}
                          </span>
                        )}
                      </td>
                      <td className="py-2 text-left px-4 border-b">
                        {getSyncState(order) === 'na' ? (
                          <span className="text-gray-400">-</span>
                        ) : (
                          <span
                            className={`inline-block rounded px-2 py-0.5 text-xs font-semibold ${syncStateColor(getSyncState(order))}`}
                            title={
                              getSyncState(order) === 'synced'
                                ? 'Sent to fulfillment provider'
                                : getSyncState(order) === 'error'
                                  ? order?.lastFulfillmentError || 'A send was attempted and failed'
                                  : getSyncState(order) === 'bundle'
                                    ? 'Bundle of individual orders — see their rows for sync status'
                                    : 'Not sent to provider yet'
                            }
                          >
                            {syncStateLabel(getSyncState(order))}
                          </span>
                        )}
                      </td>
                      <td className="py-2 text-left px-4 border-b">
                        {isPhysicalOrder(order?.orderType) ? (
                          <FulfillmentStatusBadges status={order?.fulfillmentStatus} actionNeeded={order?.fulfillmentActionNeeded} />
                        ) : (
                          <span className="capitalize">{order?.status?.replace(/_/g, ' ') || '-'}</span>
                        )}
                      </td>
                      <td className="py-2 text-left px-4 border-b">
                        <div className="flex justify-center items-center gap-2">
                          {!order.shipLoading ? (
                            <FontAwesomeIcon
                              title="Ship selected book"
                              icon={faBox}
                              onClick={() => handleShipOrder(order)}
                              className="text-xl text-storyblue p-2 cursor-pointer"
                            />
                          ) : (
                            <Loader loader={order.shipLoading} dimentions={'w-9 h-9'} className={'px-3'} />
                          )}
                          <FontAwesomeIcon
                            icon={faEye}
                            className="cursor-pointer"
                            title="View Order Details"
                            onClick={() => setSelectedOrderDetails(order)}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {totalOrdersPages > 1 && (
                <div className="flex justify-end mt-4 gap-2">
                  <PaginationButtons
                    totalPages={totalOrdersPages}
                    currentPage={ordersPageNumber}
                    handlePageChange={handleOrdersPageChange}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-8">No orders found for this user.</div>
          )}
        </div>
      )}

      {/* Surveys Tab */}
      {activeTab === 'surveys' && (
        <div>
          {surveysLoader && (
            <div className="flex justify-center py-8">
              <Loader loader={surveysLoader} dimentions={'w-8 h-8'} />
            </div>
          )}
          {!surveysLoader && surveys.length > 0 ? (
            <div className="space-y-4">
              {surveys.map((survey, index) => (
                <div key={survey.id || index} className="p-4 bg-gray-50 rounded-md">
                  <h3 className="text-sm font-bold text-storyblue mb-3">
                    Survey {surveys.length > 1 ? `#${index + 1}` : ''}
                    {survey.completedAt && (
                      <span className="text-xs font-normal text-gray-500 ml-2">
                        Completed: {typeof survey.completedAt === 'string' ? survey.completedAt : convertTimestampToDateTime(survey.completedAt)}
                      </span>
                    )}
                  </h3>
                  <div className="space-y-0">
                    {renderSurveyField('First Use Type', survey.firstUseType)}
                    {renderSurveyField('Grades', survey.grades)}
                    {survey.customGrade && renderSurveyField('Custom Grade', survey.customGrade)}
                    {renderSurveyField('Student Count', survey.studentCount)}
                    {renderSurveyField('School Name', survey.schoolName)}
                    {renderSurveyField('School ID', survey.schoolId)}
                    {renderSurveyField('Program Type', survey.programType)}
                    {renderSurveyField('Job Role', survey.jobRole)}
                    {survey.jobRoleOther && renderSurveyField('Job Role (Other)', survey.jobRoleOther)}
                    {renderSurveyField('Country', survey.country)}
                    {renderSurveyField('State', survey.state)}
                    {renderSurveyField('City', survey.city)}
                    {renderSurveyField('Mobile Number', survey.mobileNumber)}
                    {renderSurveyField('How Found Us', survey.howFoundUs)}
                    {survey.howFoundUsOther && renderSurveyField('How Found Us (Other)', survey.howFoundUsOther)}
                    {renderSurveyField('Referral Code', survey.referralCode)}
                    {renderSurveyField('Book Cost Coverage', survey.bookCostCoverage)}
                    {renderSurveyField('Video Watch Time (sec)', survey.videoWatchTimeSeconds)}
                    {renderSurveyField('Final Survey Completed', survey.finalSurveyCompleted)}
                    {survey.finalSurveyResponses && renderSurveyField('Final Survey Responses', survey.finalSurveyResponses)}
                    {survey.createdAt && renderSurveyField('Created At', convertTimestampToDateTime(survey.createdAt))}
                    {survey.updatedAt && renderSurveyField('Updated At', convertTimestampToDateTime(survey.updatedAt))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            !surveysLoader && (
              <div className="text-center text-gray-500 py-8">No surveys found.</div>
            )
          )}
        </div>
      )}

      {/* Lead Data Tab */}
      {activeTab === 'lead' && user.lead && (
        <div className="space-y-2 p-3 bg-gray-50 rounded-md">
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Lead ID:</span>
            <span className="text-sm font-medium text-gray-800">{user.lead.leadId || '—'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Project ID:</span>
            <span className="text-sm font-medium text-gray-800">{user.lead.projectId || '—'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Email:</span>
            <span className="text-sm font-medium text-gray-800">{user.lead.email || '—'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Created At:</span>
            <span className="text-sm font-medium text-gray-800">
              {user.lead.createdAt ? convertTimestampToDateTime(user.lead.createdAt) : '—'}
            </span>
          </div>
        </div>
      )}

      {/* Date Picker Popup */}
      <NewDatePickerPopup
        isOpen={showDatePicker}
        onClose={() => {
          setShowDatePicker(false);
          setSelectedClassroomId(null);
        }}
        onDateSelected={handleDateSelected}
        selectedDate={
          getSelectedClassroom()?.activeProject?.startDate
            ? new Date(convertTimestampToDateTimeString(getSelectedClassroom()?.activeProject?.startDate))
            : undefined
        }
        showTopRightCloseButton={true}
      />
    </div>
  );
};

export default CustomerDetailsView;
